import * as React from 'react';
import { Props } from 'react';
import { connect, Dispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as queryString from 'query-string';

import { AppState } from '../../reducers';

import {
    setChannelUserIdAction,
    setUserPhoneAction,
    setUserCodeAction,
    setChannelIdAction,
    setUserLangAction
} from '../../actions/user';
import {
    authorizationAction,
    authorizationSendCodeAction,
    resetSendCodeAuthorizationAction
} from '../../actions/Authorization/authorization';

import { SendCodeView } from '../RegistrationAuthorization/SendCodeView';
import i18next from 'i18next';
import firebaseAuthObject from '../../lib/firebaseAuthObject';
import * as firebase from 'firebase';

interface RouteProps extends RouteComponentProps<{}> {
    match: any;
    history: any;
}

interface AuthorizationSendCodeContainerProps extends Props<AuthorizationSendCodeContainer> {
    sendCodeAuthorization: boolean;
    loadingSendCodeAuthorization: boolean;
    errorSendCodeAuthorization: {
        messages: string[];
        code: number;
    } | null;
    tariffId: number;

    lang: string;
    phone: string;
    code: string;

    channelUserId: string;
    channelId: string;
    isFirebaseAuth: boolean;
    authorization: boolean;
}

interface AuthorizationSendCodeContainerDispatch extends Props<AuthorizationSendCodeContainer> {
    setLang: (lang: string) => any;
    setPhone: (phone: string) => any;
    setChannelUserId: (channelUserId: string) => any;
    setChannelId: (channelId: string) => any;
    setCode: (code: string) => any;
    onAuthorizationSendCode: (body: any) => any;
    resetSendCodeAuthorization: () => any;
    onAuthorization: (phone: string, channelUserId: string) => any;
}

function mapProps(state: AppState, props: AuthorizationSendCodeContainerProps): AuthorizationSendCodeContainerProps {
    return {
        sendCodeAuthorization: state.authorizationState.sendCodeAuthorization,
        loadingSendCodeAuthorization: state.authorizationState.loadingSendCodeAuthorization,
        errorSendCodeAuthorization: state.authorizationState.errorSendCodeAuthorization,
        tariffId: state.authorizationState.tariffId,

        lang: state.userState.lang,
        phone: state.userState.phone,
        code: state.userState.code,

        channelUserId: state.userState.channelUserId,
        channelId: state.userState.channelId,
        isFirebaseAuth: state.authorizationState.isFirebaseAuth,
        authorization: state.authorizationState.authorization
    };
}

function mapDispatch(dispatch: Dispatch<any>): AuthorizationSendCodeContainerDispatch {
    return {
        setLang: (lang: string) => dispatch(setUserLangAction(lang)),
        setPhone: (phone: string) => dispatch(setUserPhoneAction(phone)),
        setChannelUserId: (channelUserId: string) => dispatch(setChannelUserIdAction(channelUserId)),
        setChannelId: (channelId: string) => dispatch(setChannelIdAction(channelId)),
        setCode: (code: string) => dispatch(setUserCodeAction(code)),
        onAuthorizationSendCode: (body) =>
            dispatch(authorizationSendCodeAction(body)),
        resetSendCodeAuthorization: () => dispatch(resetSendCodeAuthorizationAction()),
        onAuthorization: (phone: string, channelUserId: string) => dispatch(authorizationAction(phone, channelUserId))
    };
}

interface AuthorizationSendCodeContainerState {
    firebaseAuth: any | undefined;
    verifier: any | undefined;
    confirmation: any | undefined;
    isVerifierChecked: boolean;
    authError: string;
    loading: boolean;
    recaptchaVerifier: any | undefined;
}

const bankNumber = '+7 499 753 10 65';

type AllProps = AuthorizationSendCodeContainerProps & AuthorizationSendCodeContainerDispatch & RouteProps;

class AuthorizationSendCodeContainer extends React.Component<AllProps, AuthorizationSendCodeContainerState> {

    state: AuthorizationSendCodeContainerState = {
        firebaseAuth: undefined,
        verifier: undefined,
        confirmation: undefined,
        isVerifierChecked: false,
        authError: '',
        loading: false,
        recaptchaVerifier: undefined
    };

    private WRONG_CODE_MSG = i18next.t('wrong_code_message');
    private NOT_ALLOW = i18next.t('authorization_not_allowed', { phone: `<a href='tel:${bankNumber}'>${bankNumber}</a>` });
    private MAXIMUM_ATTEMPTS_EXCEEDED = i18next.t('maximum_attempts_exceeded', { phone: `<a style="color:white" href='tel:${bankNumber}'>${bankNumber}</a>` });
    private CODE_EXPIRED_MSG = i18next.t('code_expired');

    CONNECT_ERROR = (status: number) => i18next.t('error_contact_konsierge', {status}) + i18next.t('or_reload_page');

    async componentDidMount() {
        const { history, lang, isFirebaseAuth, authorization,
            setPhone, setChannelUserId, setChannelId, setLang, onAuthorization } = this.props;
        if (history.location.search) {
            const params = queryString.parse(history.location.search);
            const phone = params.phone;
            const channelUserId = params.user_id;
            const channel = params.channel_id;
            const language = params.lang;
            if (!lang) {
                setLang(language || 'ru');
            }
            if (language && language !== lang && i18next.languages.some(it => it === lang)) {
                setLang(language);
                await i18next.changeLanguage(language);
            }

            if (channel) {
                setChannelId(channel);
            }
            if (phone) {
                setPhone(phone);
            }
            if (channelUserId) {
                setChannelUserId(channelUserId);
            }
            if (!authorization) {
                this.setState({loading: true});
                await onAuthorization(phone, channelUserId);
            }
            if (isFirebaseAuth) {
                this.setState({loading: true});
                await this.connectFirebase();
                this.setTimer();
            }
        }
    }

    setTimer = () => {
        const { history } = this.props;
        if (history.location.search) {
            const params = queryString.parse(history.location.search);
            const phone = params.phone;
            const channelUserId = params.user_id;
            const channel = params.channel_id;
            const lang = params.lang;
            setTimeout(() => {
                this.setState({authError: this.CODE_EXPIRED_MSG});
                setTimeout(() => history.push(`/authorization?channel_id=${channel}&phone=${phone}&user_id=${encodeURIComponent(channelUserId)}&lang=${lang}`), 5000);
            }, 300000);
        }
    }

    async componentDidUpdate(prevProps: Readonly<AllProps>, prevState: Readonly<AuthorizationSendCodeContainerState>, snapshot?: any) {
        const { phone, isFirebaseAuth, authorization } = this.props;
        const { firebaseAuth, verifier, confirmation } = this.state;
        if (isFirebaseAuth && !prevProps.isFirebaseAuth) {
            this.setState({loading: true});
            this.connectFirebase();
            this.setTimer();
        }
        if (authorization && authorization !== prevProps.authorization && !isFirebaseAuth) {
            this.setState({loading: false});
        }
        if (firebaseAuth && !prevState.firebaseAuth) {
            this.initializeRecaptcha();
        }
        /*if (verifier && verifier !== prevState.verifier) {
            setTimeout(() => this.verifyNumber(), 5000);
        }*/
        if (verifier && verifier !== prevState.verifier
            && firebaseAuth && phone && verifier) {
            this.signInFirebase();
        }
        if (confirmation && confirmation !== prevState.confirmation) {
            this.setState({loading: false});
        }
    }

    componentWillUnmount() {
        const {resetSendCodeAuthorization} = this.props;
        resetSendCodeAuthorization();
    }

    componentWillReceiveProps(nextProps: AllProps) {
        const { sendCodeAuthorization, history, phone, channelUserId, channelId, lang, tariffId } = nextProps;
        if (sendCodeAuthorization && (sendCodeAuthorization !== this.props.sendCodeAuthorization)) {
            history.push(`/authorization/success?channel_id=${channelId}&phone=${phone}&user_id=${encodeURIComponent(channelUserId)}&lang=${lang}&tariff_id=${tariffId}`);
        }
    }

    connectFirebase = async () => {
        try {
            const firebaseAuth = firebase.initializeApp(firebaseAuthObject);
            this.setState({firebaseAuth});
        } catch (e) {
            console.log(e);
            const firebaseAuth = firebase.app();
            this.setState({firebaseAuth});
        } finally {
            firebase.auth().useDeviceLanguage();
        }
    }

    initializeRecaptcha = async () => {
        try {
            var applicationVerifier = new firebase.auth.RecaptchaVerifier(
                'recaptcha-container', {
                    size: 'invisible',
                    callback: () => {
                        this.setState({isVerifierChecked: true});
                    }
                });
            await applicationVerifier.verify();
            this.setState({verifier: applicationVerifier});
        } catch (e) {
            console.log(e);
            this.setState({loading: false, authError: this.CONNECT_ERROR(405)});
        }
    }

    verifyNumber = async () => {
        const { phone } = this.props;
        const { verifier } = this.state;
        try {
            const provider = new firebase.auth.PhoneAuthProvider();
            setTimeout(() => provider.verifyPhoneNumber(`+${phone}`, verifier)
                .then(() => {
                    console.log('ok number');
                }), 1000);
        } catch (e) {
            console.log(e);
            this.setState({loading: false, authError: this.CONNECT_ERROR(409)});
        }
    }

    signInFirebase = () => {
        const { phone } = this.props;
        const { firebaseAuth, verifier } = this.state;
        if (firebaseAuth && verifier && phone) {
            firebase.auth().signInWithPhoneNumber(`+${phone}`, verifier)
                .then((confirmationResult) => {
                    if (!confirmationResult) {
                        this.setState({authError: this.CONNECT_ERROR(401)});
                    } else {
                        this.setState({confirmation: confirmationResult});
                    }
                }).catch((error) => {
                    console.log(error);
                    this.setState({authError: this.CONNECT_ERROR(401)});
            });
        } else {
            console.log('unset confirmation');
            this.setState({confirmation: undefined, authError: this.CONNECT_ERROR(402)});
        }
    }

    onFirebaseSendCode = (code: string) => {
        const { phone, channelUserId, onAuthorizationSendCode } = this.props;
        const { confirmation } = this.state;
        if (!confirmation) {
            alert(this.CONNECT_ERROR(400));
            return;
        }
        confirmation!.confirm(code).then((result) => {
            // User signed in successfully.
            const user: any = result.user;
            console.log('success');
            onAuthorizationSendCode(JSON.stringify({
                userId: channelUserId,
                phone,
                firebaseToken: user.ma || user._lat,
                services: (localStorage.getItem('re-authorization')) ? localStorage.getItem('re-authorization') === 'dif_msngr' : false
            }));
        }).catch(() => {
            this.setState({authError: this.WRONG_CODE_MSG});
        });
    }

    onSendCode = (code: string) => {
        const { isFirebaseAuth, phone, channelUserId, onAuthorizationSendCode } = this.props;
        if (isFirebaseAuth) {
            this.onFirebaseSendCode(code);
        } else {
            onAuthorizationSendCode(JSON.stringify({
                userId: channelUserId,
                phone,
                code,
                services: (localStorage.getItem('re-authorization')) ? localStorage.getItem('re-authorization') === 'dif_msngr' : false
            }));
        }
    }

    render() {
        document.title = i18next.t('authorization');
        const { phone, code, setCode, channelUserId, channelId, lang, isFirebaseAuth } = this.props;
        const { loadingSendCodeAuthorization, errorSendCodeAuthorization, history } = this.props;
        const { authError, loading } = this.state;
        let error = '';

        if (authError) {
            error = authError;
        } else if (errorSendCodeAuthorization) {
            if (errorSendCodeAuthorization.code === 21) {
                error = this.MAXIMUM_ATTEMPTS_EXCEEDED;
            }
            if (errorSendCodeAuthorization.code === 23) {
                error = this.WRONG_CODE_MSG;
            }
            if (errorSendCodeAuthorization.code === 0) {
                error = this.NOT_ALLOW;
            }
            if (errorSendCodeAuthorization.code === 27) {
                error = this.NOT_ALLOW;
            }
        }

        if (errorSendCodeAuthorization && errorSendCodeAuthorization.code === 22) {
            history.push(`/authorization/isSendCode?channel_id=${channelId}&phone=${phone}&user_id=${encodeURIComponent(channelUserId)}&lang=${lang}`);
        }

        return (
            <SendCodeView
                channelId={channelId}
                name={i18next.t('authorization')}
                code={code}
                setCode={(e) => {
                    setCode(e);
                    if (authError) {
                        this.setState({authError: ''});
                    }
                }}
                onSendCode={(newCode: string) => this.onSendCode(newCode)}
                error={error}
                codeLength={isFirebaseAuth ? 6 : 4}
                loading={loadingSendCodeAuthorization || loading}
                isFirebaseAuth={isFirebaseAuth}
            />
        );
    }
}

export default withRouter(connect(mapProps, mapDispatch)(AuthorizationSendCodeContainer));



// WEBPACK FOOTER //
// ./src/components/Authorization/AuthorizationSendCodeContainer.tsx